import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';
import { Calendar as CalendarIcon, Clock, BookOpen, Users, DollarSign, Video, Radio, FileQuestion, Layers } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { format, isAfter, isBefore, isWithinInterval, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface Course {
  id: string;
  title: string;
  description: string;
  price: number;
  thumbnail_url: string | null;
  course_type: string;
  start_date: string | null;
  end_date: string | null;
  is_published: boolean;
  category_id: string;
  profiles: {
    full_name: string;
  };
  categories: {
    name: string;
  };
}

const courseTypeIcons = {
  video: Video,
  live: Radio,
  quiz: FileQuestion,
  mixed: Layers,
};

const courseTypeLabels = {
  video: 'Video Corso',
  live: 'Live Corso',
  quiz: 'Quiz',
  mixed: 'Misto',
};

export default function CalendarPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');

  useEffect(() => {
    if (profile) {
      fetchCourses();
      fetchEnrollments();
    }
  }, [profile]);

  const fetchCourses = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        profiles:teacher_id(full_name),
        categories:category_id(name)
      `)
      .eq('is_published', true)
      .order('start_date', { ascending: true });

    if (error) {
      console.error('Error fetching courses:', error);
    }

    if (data) {
      setCourses(data);
    }
    setLoading(false);
  };

  const fetchEnrollments = async () => {
    const { data } = await supabase
      .from('enrollments')
      .select('course_id')
      .eq('student_id', profile?.id);

    if (data) {
      setEnrolledCourseIds(new Set(data.map(e => e.course_id)));
    }
  };

  const getCoursesForDate = (date: Date) => {
    return courses.filter(course => {
      if (!course.start_date) return false;
      const startDate = new Date(course.start_date);
      const endDate = course.end_date ? new Date(course.end_date) : startDate;

      return isWithinInterval(date, { start: startDate, end: endDate }) ||
             isSameDay(date, startDate) ||
             isSameDay(date, endDate);
    });
  };

  const upcomingCourses = courses.filter(course => {
    if (!course.start_date) return false;
    return isAfter(new Date(course.start_date), new Date());
  });

  const ongoingCourses = courses.filter(course => {
    if (!course.start_date) return false;
    const now = new Date();
    const start = new Date(course.start_date);
    const end = course.end_date ? new Date(course.end_date) : start;

    return isBefore(start, now) && isAfter(end, now);
  });

  const allAvailableCourses = courses.filter(course => course.is_published);

  const handleEnroll = (courseId: string) => {
    navigate(`/dashboard/courses/${courseId}`);
  };

  const renderCalendarView = () => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Calendario Corsi</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1))}
              >
                ←
              </Button>
              <span className="px-4 py-2 font-medium">
                {format(selectedDate, 'MMMM yyyy', { locale: it })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1))}
              >
                →
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
              <div key={day} className="text-center text-sm font-semibold text-gray-600 p-2">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              const coursesForDay = getCoursesForDate(day);
              const isToday = isSameDay(day, new Date());

              return (
                <div
                  key={index}
                  className={cn(
                    'min-h-24 p-2 border rounded-lg',
                    !isSameMonth(day, selectedDate) && 'bg-gray-50 text-gray-400',
                    isToday && 'border-blue-600 bg-blue-50',
                    coursesForDay.length > 0 && 'bg-green-50'
                  )}
                >
                  <div className="text-sm font-medium mb-1">{format(day, 'd')}</div>
                  {coursesForDay.length > 0 && (
                    <div className="space-y-1">
                      {coursesForDay.slice(0, 2).map(course => (
                        <div
                          key={course.id}
                          className="text-xs bg-blue-600 text-white rounded px-1 py-0.5 truncate cursor-pointer"
                          onClick={() => handleEnroll(course.id)}
                        >
                          {course.title}
                        </div>
                      ))}
                      {coursesForDay.length > 2 && (
                        <div className="text-xs text-gray-600">
                          +{coursesForDay.length - 2} altri
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderCourseCard = (course: Course) => {
    const Icon = courseTypeIcons[course.course_type as keyof typeof courseTypeIcons] || BookOpen;
    const isEnrolled = enrolledCourseIds.has(course.id);
    const hasStartDate = course.start_date !== null;

    return (
      <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
        <div className="aspect-video bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center relative">
          {course.thumbnail_url ? (
            <img
              src={course.thumbnail_url}
              alt={course.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <Icon className="w-16 h-16 text-white opacity-50" />
          )}
          <div className="absolute top-2 left-2">
            <Badge variant="secondary">
              {courseTypeLabels[course.course_type as keyof typeof courseTypeLabels]}
            </Badge>
          </div>
          {isEnrolled && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-green-600">Iscritto</Badge>
            </div>
          )}
        </div>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-lg line-clamp-2">{course.title}</CardTitle>
          </div>
          <CardDescription className="line-clamp-2">
            {course.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center text-gray-600">
                <Users className="w-4 h-4 mr-1" />
                {course.profiles?.full_name}
              </div>
              {course.categories && (
                <Badge variant="outline">{course.categories.name}</Badge>
              )}
            </div>

            {hasStartDate && course.start_date && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-600">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  <span>
                    Inizio: {format(new Date(course.start_date), 'dd MMM yyyy', { locale: it })}
                  </span>
                </div>
                {course.end_date && (
                  <div className="flex items-center text-gray-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>
                      Fine: {format(new Date(course.end_date), 'dd MMM yyyy', { locale: it })}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t">
              <div className="flex items-center text-lg font-bold text-blue-600">
                <DollarSign className="w-5 h-5" />
                {course.price.toFixed(2)}
              </div>
              <Button
                onClick={() => handleEnroll(course.id)}
                disabled={isEnrolled}
                variant={isEnrolled ? 'secondary' : 'default'}
              >
                {isEnrolled ? 'Già iscritto' : 'Iscriviti'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (profile?.role !== 'studente') {
    navigate('/dashboard');
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Calendario Corsi
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Visualizza tutti i corsi disponibili e le loro date
            </p>
          </div>
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as any)}>
            <TabsList>
              <TabsTrigger value="list">Lista</TabsTrigger>
              <TabsTrigger value="calendar">Calendario</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-96" />
            ))}
          </div>
        ) : viewMode === 'calendar' ? (
          renderCalendarView()
        ) : (
          <Tabs defaultValue="all" className="space-y-6">
            <TabsList>
              <TabsTrigger value="all">
                Tutti ({allAvailableCourses.length})
              </TabsTrigger>
              <TabsTrigger value="upcoming">
                In arrivo ({upcomingCourses.length})
              </TabsTrigger>
              <TabsTrigger value="ongoing">
                In corso ({ongoingCourses.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-6">
              {allAvailableCourses.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <BookOpen className="w-12 h-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Nessun corso disponibile
                    </h3>
                    <p className="text-gray-600 text-center">
                      Non ci sono corsi disponibili al momento
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {allAvailableCourses.map(renderCourseCard)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="upcoming" className="space-y-6">
              {upcomingCourses.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <CalendarIcon className="w-12 h-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Nessun corso in arrivo
                    </h3>
                    <p className="text-gray-600 text-center">
                      Non ci sono corsi programmati per il futuro
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {upcomingCourses.map(renderCourseCard)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="ongoing" className="space-y-6">
              {ongoingCourses.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Clock className="w-12 h-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Nessun corso attivo
                    </h3>
                    <p className="text-gray-600 text-center">
                      Non ci sono corsi attualmente in svolgimento
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {ongoingCourses.map(renderCourseCard)}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
